import React from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import { Box, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PropTypes from "prop-types";

function OnboardingProgressBar({ steps, currentProgress }) {
  // Calculate completion percentage
  const totalSteps = steps.length;
  const completedSteps = steps.filter((step) => step.completed).length;
  const progressPercentage = (completedSteps / totalSteps) * 100;

  // Debug logs
  console.log("🔍 Progress Bar Debug:");
  console.log("Total Steps:", totalSteps);
  console.log("Completed Steps:", completedSteps);
  console.log("Progress Percentage:", progressPercentage);
  console.log("Steps Array:", steps);

  return (
    <ArgonBox mb={2}>
      {/* Progress Bar Header */}
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <ArgonTypography variant="button" fontWeight="medium" color="dark">
          Onboarding Progress
        </ArgonTypography>
        <ArgonTypography variant="caption" fontWeight="bold" color="success">
          {completedSteps}/{totalSteps}
        </ArgonTypography>
      </ArgonBox>

      {/* Linear Progress Bar */}
      <ArgonBox mb={1.5}>
        <Box
          sx={{
            width: "100%",
            height: 8,
            borderRadius: 4,
            backgroundColor: "#e0e0e0",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: `${progressPercentage}%`,
              height: "100%",
              borderRadius: 4,
              background: "linear-gradient(90deg, #4CAF50 0%, #81C784 100%)",
              transition: "width 0.6s ease-in-out",
            }}
          />
        </Box>
      </ArgonBox>

      {/* Step Indicators */}
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" gap={0.5}>
        {steps.map((step, index) => (
          <Tooltip
            key={index}
            title={step.completed ? `${step.label} - Completed ✓` : `${step.label} - Pending`}
            arrow
            placement="top"
          >
            <ArgonBox
              display="flex"
              alignItems="center"
              flex={1}
              sx={{
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {/* Icon */}
              {step.completed ? (
                <CheckCircleIcon
                  sx={{
                    fontSize: 18,
                    color: "#4CAF50",
                  }}
                />
              ) : (
                <RadioButtonUncheckedIcon
                  sx={{
                    fontSize: 18,
                    color: "#bdbdbd",
                  }}
                />
              )}

              {/* Step Label */}
              <ArgonTypography
                variant="caption"
                fontWeight="medium"
                color={step.completed ? "success" : "secondary"}
                sx={{
                  fontSize: "0.7rem",
                  ml: 0.5,
                }}
              >
                {step.label}
              </ArgonTypography>
            </ArgonBox>
          </Tooltip>
        ))}
      </ArgonBox>
    </ArgonBox>
  );
}

OnboardingProgressBar.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      completed: PropTypes.bool.isRequired,
    })
  ).isRequired,
  currentProgress: PropTypes.number,
};

OnboardingProgressBar.defaultProps = {
  currentProgress: 0,
};

export default OnboardingProgressBar;
